/**
 * Leaderboard Page Component - Redesigned
 * Shows top players with game-specific ratings and streaks
 */

import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { userAPI } from '../services/api';

import Footer from '../components/Footer';
import '../styles/Dashboard.css';

const GAME_MODES = [
  { id: 'overall', label: 'Overall', icon: '🏆' },
  { id: 'chess', label: 'Chess', icon: '♟️' },
  { id: 'checkers', label: 'Checkers', icon: '🏁' },
  { id: 'draughts', label: 'Draughts', icon: '🛑' },
  { id: 'whot', label: 'Whot', icon: '🃏' },
  { id: 'flappy', label: 'Flappy', icon: '🐦' },
  { id: 'reaction', label: 'Reaction', icon: '🎨' },
];

const Leaderboard = () => {
  const [leaderboard, setLeaderboard] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [sortBy, setSortBy] = useState('rating');
  const [viewMode, setViewMode] = useState('list'); // 'list' or 'grid'
  const [gameMode, setGameMode] = useState('overall');
  const navigate = useNavigate();

  useEffect(() => {
    fetchLeaderboard();
  }, [gameMode]);

  const fetchLeaderboard = async () => {
    try {
      setLoading(true);
      const response = await userAPI.getLeaderboard(gameMode, 10);
      setLeaderboard(response.data || []);
      setError('');
      
      // Default sort by wins for overall, rating for games
      if (gameMode === 'overall') {
        setSortBy('wins');
      } else {
        setSortBy('rating');
      }
    } catch (error) {
      console.error('Failed to fetch leaderboard:', error);
      setError(error?.response?.data?.error || 'Failed to load leaderboard');
    } finally {
      setLoading(false);
    }
  };

  const playersWithStats = useMemo(() => {
    return leaderboard.map(player => {
      const wins = Number(player.wins) || 0;
      const losses = Number(player.losses) || 0;
      const total = wins + losses;
      const winRate = total > 0 ? ((wins / total) * 100).toFixed(1) : 0;
      
      // Game specific rating/streak
      const rating = gameMode === 'overall' ? null : (player[`${gameMode}_rating`] || 1200);
      const streak = gameMode === 'overall' ? null : (player[`${gameMode}_streak`] || 0);

      return {
        ...player,
        wins,
        losses,
        total,
        winRate,
        winRateNum: parseFloat(winRate),
        rating,
        streak
      };
    });
  }, [leaderboard, gameMode]);

  const filteredAndSortedPlayers = useMemo(() => {
    let filtered = playersWithStats;
    if (searchTerm.trim()) {
      const term = searchTerm.toLowerCase().trim();
      filtered = filtered.filter(p => p.username?.toLowerCase().includes(term));
    }
    
    filtered = [...filtered].sort((a, b) => {
      if (sortBy === 'rating') return (b.rating || 0) - (a.rating || 0);
      if (sortBy === 'streak') return (b.streak || 0) - (a.streak || 0);
      if (sortBy === 'wins') return b.wins - a.wins;
      if (sortBy === 'winRate') return b.winRateNum - a.winRateNum;
      return b.wins - a.wins;
    });
    
    return filtered;
  }, [playersWithStats, searchTerm, sortBy]);

  const getRankBadge = (index) => {
    if (index === 0) return '🥇';
    if (index === 1) return '🥈';
    if (index === 2) return '🥉';
    return `#${index + 1}`;
  };

  const getRankClass = (index) => {
    if (index === 0) return 'rank-1';
    if (index === 1) return 'rank-2';
    if (index === 2) return 'rank-3';
    return '';
  };

  return (
    <div className="dashboard-container dashboard-redesign">
      <div className="animated-bg">
        <div className="gradient-orb orb-1"></div>
        <div className="gradient-orb orb-2"></div>
      </div>

      <div className="dashboard-content">
        <div className="leaderboard-header">
          <div className="header-left">
            <div className="header-icon">🏆</div>
            <div>
              <h1 className="page-title">Leaderboard</h1>
              <p className="page-subtitle">Rankings: {GAME_MODES.find(m => m.id === gameMode)?.label}</p>
            </div>
          </div>
          <button className="back-btn" onClick={() => navigate('/dashboard')}>← Dashboard</button>
        </div>

        {/* Game Mode Selector */}
        <div className="game-mode-tabs" style={{ display: 'flex', gap: 10, overflowX: 'auto', paddingBottom: 10, marginBottom: 20 }}>
          {GAME_MODES.map(mode => (
            <button 
              key={mode.id}
              className={`mode-tab ${gameMode === mode.id ? 'active' : ''}`}
              onClick={() => setGameMode(mode.id)}
              style={{
                padding: '10px 16px',
                borderRadius: '12px',
                border: '1px solid rgba(255,255,255,0.1)',
                background: gameMode === mode.id ? 'rgba(255,255,255,0.1)' : 'transparent',
                color: 'white',
                cursor: 'pointer',
                whiteSpace: 'nowrap',
                display: 'flex',
                alignItems: 'center',
                gap: 8,
                transition: 'all 0.2s'
              }}
            >
              <span>{mode.icon}</span> {mode.label}
            </button>
          ))}
        </div>

        {!loading && !error && (
          <div className="leaderboard-filters" style={{ marginBottom: 20 }}>
            <div className="search-wrapper">
              <input
                type="text"
                className="search-input"
                placeholder="Search players..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
            </div>
            
            <div className="filter-group">
              <select className="sort-select" value={sortBy} onChange={(e) => setSortBy(e.target.value)}>
                {gameMode !== 'overall' && <option value="rating">Sort by Rating</option>}
                {gameMode !== 'overall' && <option value="streak">Sort by Win Streak</option>}
                <option value="wins">Sort by Total Wins</option>
                <option value="winRate">Sort by Win Rate</option>
              </select>
              
              <div className="view-toggle">
                <button className={`view-btn ${viewMode === 'list' ? 'active' : ''}`} onClick={() => setViewMode('list')}>📋 List</button>
                <button className={`view-btn ${viewMode === 'grid' ? 'active' : ''}`} onClick={() => setViewMode('grid')}>🔲 Grid</button>
              </div>
            </div>
          </div>
        )}

        {loading ? (
          <div className="loading-state"><div className="spinner"></div><span>Loading rankings...</span></div>
        ) : error ? (
          <div className="error-alert">⚠️ {error}</div>
        ) : viewMode === 'grid' ? (
          <div className="leaderboard-grid">
            {filteredAndSortedPlayers.map((player, index) => (
              <div key={player.id} className={`leaderboard-card ${getRankClass(index)}`}>
                <div className="card-rank">
                  <span className={`rank-badge-large ${getRankClass(index)}`}>{getRankBadge(index)}</span>
                </div>
                <div className="card-avatar">
                  <div className="avatar-large">{player.username?.charAt(0).toUpperCase()}</div>
                </div>
                <div className="card-name">{player.username}</div>
                <div className="card-stats">
                  {gameMode !== 'overall' && (
                    <>
                      <div className="stat-group">
                        <div className="stat-label">Rating</div>
                        <div className="stat-value">⭐{player.rating}</div>
                      </div>
                      <div className="stat-divider"></div>
                      <div className="stat-group">
                        <div className="stat-label">Streak</div>
                        <div className="stat-value">{player.streak >= 3 ? `🔥${player.streak}` : '—'}</div>
                      </div>
                      <div className="stat-divider"></div>
                    </>
                  )}
                  <div className="stat-group">
                    <div className="stat-label">Wins</div>
                    <div className="stat-value wins">{player.wins}</div>
                  </div>
                  <div className="stat-divider"></div>
                  <div className="stat-group">
                    <div className="stat-label">Win Rate</div>
                    <div className="stat-value">{player.winRate}%</div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div className="leaderboard-table-container">
            <table className="leaderboard-table">
              <thead>
                <tr>
                  <th className="rank-col">Rank</th>
                  <th className="player-col">Player</th>
                  {gameMode !== 'overall' && <th>Rating</th>}
                  {gameMode !== 'overall' && <th>Streak</th>}
                  <th>Wins</th>
                  <th>Win Rate</th>
                </tr>
              </thead>
              <tbody>
                {filteredAndSortedPlayers.map((player, index) => (
                  <tr key={player.id} className={`leaderboard-row ${getRankClass(index)}`}>
                    <td><span className={`rank-badge ${getRankClass(index)}`}>{getRankBadge(index)}</span></td>
                    <td>
                      <div className="player-info">
                        <div className="player-avatar">{player.username?.charAt(0).toUpperCase()}</div>
                        <span className="player-name">{player.username}</span>
                      </div>
                    </td>
                    {gameMode !== 'overall' && <td>⭐ {player.rating}</td>}
                    {gameMode !== 'overall' && <td>{player.streak >= 3 ? `🔥 ${player.streak}` : '—'}</td>}
                    <td>{player.wins}</td>
                    <td>{player.winRate}%</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
      <Footer />
    </div>
  );
};

export default Leaderboard;